import { API_URL } from './api';

export interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    creditsRemaining: number;
    isAdmin: boolean;
  };
}

export const authService = {
  signup: async (data: SignupData): Promise<AuthResponse> => {
    const response = await fetch(`${API_URL}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json();
        throw new Error(error.message || 'Signup failed');
      } else {
        const text = await response.text();
        console.error('Non-JSON error response:', text);
        throw new Error(`Server returned an unexpected response (HTML). Please check if your API URL (${API_URL}) is correct.`);
      }
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const result = await response.json();
      return result.data;
    } else {
      throw new Error('Server returned an unexpected response (HTML) instead of JSON.');
    }
  },

  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      } else {
        const text = await response.text();
        console.error('Non-JSON error response:', text);
        throw new Error(`Server returned an unexpected response (HTML). Please check if your API URL (${API_URL}) is correct.`);
      }
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const result = await response.json();
      return result.data;
    } else {
      throw new Error('Server returned an unexpected response (HTML) instead of JSON.');
    }
  },

  googleExchange: async (code: string): Promise<AuthResponse> => {
    const response = await fetch(`${API_URL}/api/auth/google/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json();
        throw new Error(error.message || 'Google exchange failed');
      } else {
        const text = await response.text();
        console.error('Non-JSON error response:', text);
        throw new Error(`Server returned an unexpected response. Please check if your API URL (${API_URL}) is correct.`);
      }
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const result = await response.json();
      return result.data;
    } else {
      throw new Error('Server returned an unexpected response (HTML) instead of JSON.');
    }
  },

  logout: async (): Promise<void> => {
    const token = localStorage.getItem('token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        console.error('Failed to notify backend of logout:', error);
      }
    }
  },

  getToken: () => localStorage.getItem('token'),
  
  getUser: () => {
    const user = localStorage.getItem('user');
    if (!user || user === 'undefined') return null;
    try {
      return JSON.parse(user);
    } catch (e) {
      console.error('Failed to parse user from localStorage', e);
      return null;
    }
  },

  getProfile: async (): Promise<AuthResponse['user']> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No token found');

    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) throw new Error('Failed to fetch profile');
    const result = await response.json();
    return result.data;
  }
};
